/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatNumber } from '@angular/common';
import { Component, Injectable, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { IStatusPanelAngularComp } from 'ag-grid-angular';
import { IStatusPanelParams } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { ɵ$localize } from '@angular/localize';

export interface IAgPaginator {
  setPageSize(pageSize?: number): any;
  goToCurrentPage(): any;
  resetPaginator(): any;
}

@Injectable()
export class AgPaginatorIntl implements MatPaginatorIntl {
  changes = new Subject<void>();

  // For internationalization, the `$localize` function from
  // the `@angular/localize` package can be used.
  firstPageLabel = ɵ$localize`First page`;
  itemsPerPageLabel = ɵ$localize`Items per page:`;
  lastPageLabel = ɵ$localize`Last page`;

  // You can set labels to an arbitrary string too, or dynamically compute
  // it through other third-party internationalization libraries.
  nextPageLabel = 'Next page';
  previousPageLabel = 'Previous page';

  getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0) {
      return ɵ$localize`Page 1 of 1`;
    }

    return ɵ$localize`${formatNumber(page * pageSize, 'en')} - ${formatNumber((page * pageSize) + pageSize, 'en')} of ${formatNumber(length, 'en')}`;
  }
}

@Component({
    selector: 'app-ag-paginator',
    templateUrl: './ag-paginator.component.html',
    styleUrls: ['./ag-paginator.component.scss'],
    imports: [MatPaginator]
})
export class AgPaginatorComponent implements IStatusPanelAngularComp {

  params?: IStatusPanelParams<any, any>;
  isClientSide = false;
  length?: number = 0;
  pageSize?: number;
  pageSizeOptions: number[] = [];
  currentIndex = 0;

  pageEvent?: PageEvent;

  @ViewChild(MatPaginator) paginator?: MatPaginator;

  agInit(params: IStatusPanelParams<any, any>): void {
    this.pageSizeOptions = params.context?.pageSizeOptions || [25, 50, 100, 150, 300];
    this.pageSize = params.context?.pageSize || 25;
    this.params = params;
    this.isClientSide = this.params?.api?.getGridOption('rowModelType') === 'clientSide';

    // This panel replaces AG Grid's page size selector; disabling it stops AG Grid warning (#94/#95) about our page sizes.
    if (this.params?.api?.getGridOption('paginationPageSizeSelector') !== false) {
      this.params?.api?.setGridOption('paginationPageSizeSelector', false);
    }

    if (this.params?.api?.paginationGetPageSize() !== this.pageSize) {
      this.params?.api?.setGridOption('paginationPageSize', this.pageSize);
    }

    if(!this.isClientSide && this.params?.api?.getGridOption('cacheBlockSize') !== this.pageSize) {
      this.params?.api?.setGridOption('cacheBlockSize', this.pageSize);
    }

    this.params?.api?.addEventListener('modelUpdated', () => {
      // modelUpdated can fire after the grid is destroyed (e.g. a datasource response resolving after navigating away).
      if (this.params?.api?.isDestroyed()) {
        return;
      }
      this.length = this.params?.api?.paginationGetRowCount();
      this.pageSize = this.params?.api?.paginationGetPageSize();
    });
  }
  
  onPage(pageEvent?: PageEvent) {

    if(pageEvent?.pageSize != this.pageSize) {
      this.pageSize = pageEvent?.pageSize ?? 25;
      if (this.params?.api?.paginationGetPageSize() !== this.pageSize) {
        this.params?.api?.setGridOption('paginationPageSize', this.pageSize);
      }

      if(!this.isClientSide && this.params?.api?.getGridOption('cacheBlockSize') !== this.pageSize) {
        this.params?.api?.setGridOption('cacheBlockSize', this.pageSize);
      }
      
      this.params?.context.componentParent.onPaginatorPageSizeChange(this.pageSize);
    }

    if(pageEvent?.pageIndex != this.currentIndex) {
      this.currentIndex = pageEvent?.pageIndex ?? 0;
      this.params?.api?.paginationGoToPage(pageEvent?.pageIndex ?? 0);
    }
  }

  setPageSize(pageSize?: number) {
    this.onPage({pageSize: pageSize ?? 25, pageIndex: 0, previousPageIndex: 0, length: 0});
  }

  goToCurrentPage() {
    this.params?.api?.paginationGoToPage(this.currentIndex);
  }

  resetPaginator() {
    this.currentIndex = 0;
    this.paginator?.firstPage();
  }

}
